import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Upload, Zap, Shield, Download, ArrowRight, Check, Menu, X,
    Star, Users, Image as ImageIcon, Clock, Lock, Sparkles,
    TrendingUp, Award, ChevronDown, Crown, Loader2, AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Landing = () => {
    const { user } = useAuth();
    const { settings, loading } = useSettings();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [openFaq, setOpenFaq] = useState(null);

    // Upload functionality state
    const [selectedFile, setSelectedFile] = useState(null);
    const [originalPreview, setOriginalPreview] = useState(null);
    const [processedImage, setProcessedImage] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [pageDragActive, setPageDragActive] = useState(false);
    const fileInputRef = useRef(null);
    const uploadSectionRef = useRef(null);
    const dragCounter = useRef(0);

    const getIcon = (iconName) => {
        const icons = {
            zap: <Zap className="w-6 h-6" />,
            shield: <Shield className="w-6 h-6" />,
            download: <Download className="w-6 h-6" />,
            upload: <Upload className="w-6 h-6" />
        };
        return icons[iconName] || <Zap className="w-6 h-6" />;
    };

    const businessName = settings?.business_name || 'BGRemove';
    const tagline = settings?.tagline || 'Remove Backgrounds. Keep the Soul.';
    const description = settings?.description || 'Transform your images in seconds. Our AI precisely removes backgrounds while preserving every detail that matters.';
    const heroImage = settings?.hero_image_url || 'https://images.unsplash.com/photo-1767439567636-792a76f6e4b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxwb3J0cmFpdCUyMHByb2Zlc3Npb25hbCUyMHdvbWFuJTIwc3R1ZGlvJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzY4NDc1MjM0fDA&ixlib=rb-4.1.0&q=85';
    const featureImage1 = settings?.feature_image_1 || 'https://images.unsplash.com/photo-1558121556-2a39fc528338';
    const featureImage2 = settings?.feature_image_2 || 'https://images.unsplash.com/photo-1747979985276-c028e567797a';
    const features = settings?.features || [
        { icon: 'zap', title: 'Lightning Fast', description: 'Remove backgrounds in under 3 seconds with our AI-powered engine.' },
        { icon: 'shield', title: 'Privacy First', description: 'Your images are processed securely and never stored permanently.' },
        { icon: 'download', title: 'HD Quality', description: 'Download high-resolution transparent PNGs ready for any use.' }
    ];
    const useCases = settings?.use_cases || [
        'E-commerce product photography',
        'Social media content creation',
        'Professional headshots',
        'Marketing materials',
        'Design mockups'
    ];
    const footerText = settings?.footer_text || '© 2024 BGRemove. All rights reserved.';
    const freeLimit = 2; // Always 2 free credits as documented in README
    const logoUrl = settings?.logo_url;
    const subscriptionPrice = settings?.subscription_price || 8.99; // Default to 100 credits/month plan

    const testimonials = [
        {
            name: "Sarah Johnson",
            role: "E-commerce Store Owner",
            content: "This tool saved me hundreds of dollars in photo editing costs. The quality is amazing and it's so fast!",
            rating: 5,
            avatar: "SJ"
        },
        {
            name: "Michael Chen",
            role: "Social Media Manager",
            content: "I process over 50 images a day. This tool has cut my editing time by 90%. Absolute game changer.",
            rating: 5,
            avatar: "MC"
        },
        {
            name: "Emily Rodriguez",
            role: "Graphic Designer",
            content: "The precision is incredible. It handles complex edges better than tools that cost 10x more.",
            rating: 5,
            avatar: "ER"
        }
    ];

    const faqs = [
        {
            question: "How does the AI background removal work?",
            answer: "Our advanced AI analyzes your image to precisely identify the subject and background, then removes the background while preserving all the fine details like hair, fur, and transparent objects."
        },
        {
            question: "What image formats are supported?",
            answer: "We support JPG, PNG, and WebP formats. All processed images are exported as high-quality transparent PNG files."
        },
        {
            question: "Is my data secure?",
            answer: "Absolutely. Your images are processed securely and are never stored permanently on our servers. We take privacy seriously."
        },
        {
            question: "Can I cancel my subscription anytime?",
            answer: "Yes! You can cancel your Pro subscription at any time. No long-term commitments or hidden fees."
        },
        {
            question: "What's the difference between Free and Pro?",
            answer: `Free users get ${freeLimit} free credit${freeLimit > 1 ? 's' : ''} to try the service. Pro subscription plans offer monthly credits that automatically roll over as long as you remain subscribed, with pricing starting at $4.99/month for 50 credits.`
        }
    ];

    // Page-level drag/drop handlers
    const handlePageDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current++;
        setPageDragActive(true);
    };

    const handlePageDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current--;
        if (dragCounter.current === 0) {
            setPageDragActive(false);
        }
    };

    const handlePageDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handlePageDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current = 0;
        setPageDragActive(false);
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFile(e.dataTransfer.files[0]);
        }
    };

    // Upload zone drag handlers
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current = 0;
        setPageDragActive(false);
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFile(e.dataTransfer.files[0]);
        }
    };

    const handleFileInput = (e) => {
        if (e.target.files && e.target.files[0]) {
            handleFile(e.target.files[0]);
        }
    };

    const handleFile = (file) => {
        if (!file.type.startsWith('image/')) {
            toast.error('Please upload an image file');
            return;
        }

        // Reset state for new file (allows dropping a new image after result)
        setProcessedImage(null);
        setProcessing(false);
        setSelectedFile(file);

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setOriginalPreview(reader.result);
        };
        reader.readAsDataURL(file);

        // Scroll to upload section
        setTimeout(() => {
            uploadSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
    };

    // Auto-process image when file is selected
    useEffect(() => {
        if (selectedFile && !processedImage && !processing) {
            processImage();
        }
    }, [selectedFile]);

    const processImage = async () => {
        if (!selectedFile) return;

        setProcessing(true);
        const formData = new FormData();
        formData.append('file', selectedFile);

        try {
            const response = await axios.post(`${API}/images/try-remove-background`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setProcessedImage(response.data.image_data);
            toast.success(response.data.message || 'Background removed successfully!');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to process image');
        } finally {
            setProcessing(false);
        }
    };

    const downloadImage = () => {
        if (!processedImage) return;

        const link = document.createElement('a');
        link.href = processedImage;
        link.download = `bg_removed_${selectedFile.name.split('.')[0]}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const resetUpload = () => {
        setSelectedFile(null);
        setOriginalPreview(null);
        setProcessedImage(null);
        setProcessing(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Don't block page load - show content with defaults while settings load
    return (
        <div
            className="min-h-screen bg-background"
            onDragEnter={handlePageDragEnter}
            onDragLeave={handlePageDragLeave}
            onDragOver={handlePageDragOver}
            onDrop={handlePageDrop}
        >
            {/* Page-level drop overlay */}
            {pageDragActive && (
                <div className="fixed inset-0 z-[100] bg-primary/10 backdrop-blur-sm border-4 border-dashed border-primary flex items-center justify-center pointer-events-none">
                    <div className="bg-background/95 rounded-2xl p-8 shadow-2xl text-center">
                        <Upload className="w-12 h-12 text-primary mx-auto mb-3" />
                        <p className="font-heading font-bold text-xl">Drop your image anywhere</p>
                        <p className="text-sm text-muted-foreground mt-1">We'll remove the background instantly</p>
                    </div>
                </div>
            )}

            {/* Grain overlay */}
            <div className="grain-overlay" />

            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-lg border-b border-border">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
                            {logoUrl ? (
                                <img
                                    src={logoUrl.startsWith('http') ? logoUrl : `${process.env.REACT_APP_BACKEND_URL}${logoUrl}`}
                                    alt={businessName}
                                    className="w-8 h-8 rounded-lg object-contain"
                                />
                            ) : (
                                <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                                    <Upload className="w-4 h-4 text-primary-foreground" />
                                </div>
                            )}
                            <span className="font-heading font-bold text-xl tracking-tight">{businessName}</span>
                        </Link>

                        {/* Desktop nav */}
                        <div className="hidden md:flex items-center gap-6">
                            <Link to="/pricing" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-pricing">
                                Pricing
                            </Link>
                            {user ? (
                                <Button onClick={() => navigate('/dashboard')} className="rounded-full" data-testid="nav-dashboard-btn">
                                    Dashboard
                                </Button>
                            ) : (
                                <>
                                    <Link to="/login" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-login">
                                        Login
                                    </Link>
                                    <Button onClick={() => navigate('/register')} className="rounded-full" data-testid="nav-get-started-btn">
                                        Get Started Free
                                    </Button>
                                </>
                            )}
                        </div>

                        {/* Mobile menu button */}
                        <button
                            className="md:hidden p-2"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            data-testid="mobile-menu-btn"
                        >
                            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>

                {/* Mobile menu */}
                {mobileMenuOpen && (
                    <div className="md:hidden border-t border-border bg-background">
                        <div className="px-4 py-4 space-y-3">
                            <Link to="/pricing" className="block text-sm font-medium text-muted-foreground" data-testid="mobile-nav-pricing">
                                Pricing
                            </Link>
                            {user ? (
                                <Button onClick={() => navigate('/dashboard')} className="w-full rounded-full" data-testid="mobile-nav-dashboard-btn">
                                    Dashboard
                                </Button>
                            ) : (
                                <>
                                    <Link to="/login" className="block text-sm font-medium text-muted-foreground" data-testid="mobile-nav-login">
                                        Login
                                    </Link>
                                    <Button onClick={() => navigate('/register')} className="w-full rounded-full" data-testid="mobile-nav-get-started-btn">
                                        Get Started Free
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </nav>

            {/* Hero Section */}
            <section className="relative pt-24 sm:pt-32 pb-12 sm:pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
                {/* Animated background gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-purple-500/5 animate-gradient opacity-50" />
                <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />

                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="text-center max-w-4xl mx-auto">
                        {/* Trust badge with animation */}
                        <div className="inline-flex items-center gap-2 px-4 sm:px-5 py-2 rounded-full bg-gradient-to-r from-primary/20 via-primary/10 to-purple-500/20 text-xs sm:text-sm font-semibold text-primary mb-6 border-2 border-primary/30 shadow-xl backdrop-blur-sm animate-pulse-subtle">
                            <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
                            <span>🚀 Trusted by 10,000+ creators worldwide</span>
                        </div>

                        <h1 className="font-heading font-black text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.1] mb-6 sm:mb-8">
                            <span className="block bg-gradient-to-r from-foreground via-foreground to-foreground bg-clip-text text-transparent">
                                Remove Backgrounds.
                            </span>
                            <span className="block bg-gradient-to-r from-primary via-purple-600 to-pink-600 bg-clip-text text-transparent mt-2">
                                Keep the Soul.
                            </span>
                        </h1>

                        <p className="text-lg sm:text-xl lg:text-2xl text-muted-foreground max-w-3xl mx-auto mb-8 sm:mb-10 px-4 leading-relaxed">
                            Professional AI-powered background removal in <span className="font-bold text-primary">under 3 seconds</span>.
                            No Photoshop skills needed. No watermarks. Just <span className="font-bold text-foreground">pure magic</span>.
                        </p>

                        {/* Enhanced value props */}
                        <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 mb-10 sm:mb-12">
                            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/50 backdrop-blur-sm border border-border">
                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                    <Clock className="w-4 h-4 text-primary" />
                                </div>
                                <span className="text-sm font-semibold">2.8s avg</span>
                            </div>
                            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/50 backdrop-blur-sm border border-border">
                                <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center">
                                    <Lock className="w-4 h-4 text-green-600" />
                                </div>
                                <span className="text-sm font-semibold">100% Secure</span>
                            </div>
                            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/50 backdrop-blur-sm border border-border">
                                <div className="w-8 h-8 rounded-full bg-amber-500/10 flex items-center justify-center">
                                    <Award className="w-4 h-4 text-amber-600" />
                                </div>
                                <span className="text-sm font-semibold">4K Quality</span>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5 mb-6">
                            <Button
                                size="lg"
                                className="rounded-full px-10 sm:px-12 py-6 sm:py-7 text-base sm:text-lg w-full sm:w-auto shadow-2xl hover:shadow-primary/50 transition-all transform hover:scale-105 bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90"
                                onClick={() => navigate(user ? '/dashboard' : '/register')}
                                data-testid="hero-cta-btn"
                            >
                                <Sparkles className="mr-2 w-5 h-5" />
                                Start Free - No Sign Up Needed
                            </Button>
                            <Button
                                size="lg"
                                variant="outline"
                                className="rounded-full px-8 sm:px-10 py-6 sm:py-7 text-base sm:text-lg w-full sm:w-auto border-2 hover:bg-secondary/50"
                                onClick={() => navigate('/pricing')}
                                data-testid="hero-pricing-btn"
                            >
                                <Crown className="mr-2 w-5 h-5" />
                                See Pricing
                            </Button>
                        </div>

                        <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2">
                                <Check className="w-4 h-4 text-green-600" />
                                <span><strong className="text-foreground">{freeLimit} free credits</strong></span>
                            </div>
                            <span className="hidden sm:inline text-border">|</span>
                            <div className="flex items-center gap-2">
                                <Check className="w-4 h-4 text-green-600" />
                                <span>No credit card required</span>
                            </div>
                            <span className="hidden sm:inline text-border">|</span>
                            <div className="flex items-center gap-2">
                                <Check className="w-4 h-4 text-green-600" />
                                <span>Cancel anytime</span>
                            </div>
                        </div>
                    </div>

                    {/* Before/After Demo Section */}
                    <div className="mt-12 sm:mt-16 max-w-5xl mx-auto">
                        <div className="text-center mb-6">
                            <h3 className="text-xl sm:text-2xl font-bold mb-2">See the Difference</h3>
                            <p className="text-sm sm:text-base text-muted-foreground">
                                Professional background removal in seconds
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                            {/* Before Image */}
                            <div className="relative rounded-xl overflow-hidden border-2 border-border shadow-2xl group">
                                <img
                                    src="/demo-images/demo-before.jpg"
                                    alt="Before - Original Image"
                                    className="w-full h-64 sm:h-80 object-cover"
                                />
                                <div className="absolute top-3 left-3 bg-background/95 backdrop-blur-sm px-3 py-1.5 rounded-lg text-sm font-semibold shadow-lg">
                                    Before
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-6">
                                    <span className="text-xs font-medium">Original with background</span>
                                </div>
                            </div>

                            {/* After Image */}
                            <div className="relative rounded-xl overflow-hidden border-2 border-primary shadow-2xl bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2220%22%20height%3D%2220%22%3E%3Crect%20width%3D%2210%22%20height%3D%2210%22%20fill%3D%22%23f0f0f0%22%2F%3E%3Crect%20x%3D%2210%22%20y%3D%2210%22%20width%3D%2210%22%20height%3D%2210%22%20fill%3D%22%23f0f0f0%22%2F%3E%3C%2Fsvg%3E')] group">
                                <img
                                    src="/demo-images/demo-after.png"
                                    alt="After - Background Removed"
                                    className="w-full h-64 sm:h-80 object-cover"
                                />
                                <div className="absolute top-3 left-3 bg-primary text-primary-foreground px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1.5 shadow-lg">
                                    <Sparkles className="w-4 h-4" />
                                    After
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-6">
                                    <span className="text-xs font-medium text-primary-foreground">Background removed</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Try It Now Section */}
                    <div ref={uploadSectionRef} className="mt-12 sm:mt-16 max-w-5xl mx-auto">
                        {!selectedFile ? (
                            /* Upload Zone */
                            <div
                                className={`relative rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl border-2 transition-all ${
                                    dragActive ? 'border-primary bg-primary/5' : 'border-border bg-secondary/30'
                                }`}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                            >
                                <div className="p-8 sm:p-16 text-center">
                                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
                                        <Upload className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
                                    </div>
                                    <h3 className="font-heading font-bold text-xl sm:text-2xl mb-3">
                                        Try It Free - No Sign Up Required
                                    </h3>
                                    <p className="text-sm sm:text-base text-muted-foreground mb-6 max-w-md mx-auto">
                                        Drag and drop an image or click to upload. See the magic happen instantly!
                                    </p>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        className="hidden"
                                        accept="image/*"
                                        onChange={handleFileInput}
                                    />
                                    <Button
                                        size="lg"
                                        className="rounded-full px-8"
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        <Upload className="mr-2 w-5 h-5" />
                                        Choose Image
                                    </Button>
                                    <p className="text-xs text-muted-foreground mt-4">
                                        Supports JPG, PNG, and WebP • Max 10MB
                                    </p>
                                </div>
                            </div>
                        ) : (
                            /* Processing/Result View */
                            <div className="space-y-4">
                                <div className="grid md:grid-cols-2 gap-4">
                                    {/* Original Image */}
                                    <div className="relative rounded-xl overflow-hidden border-2 border-border bg-background">
                                        <img
                                            src={originalPreview}
                                            alt="Original"
                                            className="w-full h-64 sm:h-80 object-contain"
                                        />
                                        <div className="absolute top-3 left-3 bg-background/95 backdrop-blur-sm px-3 py-1.5 rounded-lg text-sm font-semibold">
                                            Original
                                        </div>
                                    </div>

                                    {/* Processed Image */}
                                    <div className="relative rounded-xl overflow-hidden border-2 border-primary bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2220%22%20height%3D%2220%22%3E%3Crect%20width%3D%2210%22%20height%3D%2210%22%20fill%3D%22%23f0f0f0%22%2F%3E%3Crect%20x%3D%2210%22%20y%3D%2210%22%20width%3D%2210%22%20height%3D%2210%22%20fill%3D%22%23f0f0f0%22%2F%3E%3C%2Fsvg%3E')]">
                                        {processing ? (
                                            <div className="w-full h-64 sm:h-80 flex items-center justify-center">
                                                <div className="text-center">
                                                    <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
                                                    <p className="text-sm font-medium">Processing your image...</p>
                                                    <p className="text-xs text-muted-foreground mt-1">This usually takes 2-3 seconds</p>
                                                </div>
                                            </div>
                                        ) : processedImage ? (
                                            <>
                                                <img
                                                    src={processedImage}
                                                    alt="Processed"
                                                    className="w-full h-64 sm:h-80 object-contain"
                                                />
                                                <div className="absolute top-3 left-3 bg-primary text-primary-foreground px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1.5">
                                                    <Sparkles className="w-4 h-4" />
                                                    Processed
                                                </div>
                                            </>
                                        ) : (
                                            <div className="w-full h-64 sm:h-80 flex items-center justify-center">
                                                <div className="text-center">
                                                    <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                                                    <p className="text-sm text-muted-foreground">Processing will start automatically...</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex flex-wrap gap-3 justify-center">
                                    {processedImage && (
                                        <>
                                            <Button
                                                size="lg"
                                                className="rounded-full px-8"
                                                onClick={downloadImage}
                                            >
                                                <Download className="mr-2 w-5 h-5" />
                                                Download Image
                                            </Button>
                                            <Button
                                                size="lg"
                                                variant="outline"
                                                className="rounded-full px-8"
                                                onClick={() => navigate('/register')}
                                            >
                                                <Sparkles className="mr-2 w-5 h-5" />
                                                Remove Watermark
                                            </Button>
                                        </>
                                    )}
                                    <Button
                                        size="lg"
                                        variant="outline"
                                        className="rounded-full px-8"
                                        onClick={resetUpload}
                                        disabled={processing}
                                    >
                                        Try Another Image
                                    </Button>
                                </div>

                                {/* Watermark Notice */}
                                {processedImage && (
                                    <div className="bg-secondary/50 border border-border rounded-xl p-4 text-center">
                                        <p className="text-sm text-muted-foreground">
                                            <strong className="text-foreground">Demo version includes a watermark.</strong>{' '}
                                            Sign up for free to get {freeLimit} free credit{freeLimit > 1 ? 's' : ''}, or choose a subscription plan starting at $4.99/month!
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-background to-secondary/20">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12 sm:mb-16">
                        <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-primary/10 to-purple-500/10 text-sm font-bold uppercase tracking-widest text-primary mb-6 border-2 border-primary/20">
                            <Zap className="w-4 h-4" />
                            Simple Process
                        </span>
                        <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-4 sm:mb-6">
                            How It Works
                        </h2>
                        <p className="text-base sm:text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                            Remove backgrounds in <span className="font-bold text-foreground">3 simple steps</span>. No experience needed.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8 sm:gap-10 lg:gap-12">
                        {/* Step 1 */}
                        <div className="relative group">
                            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white font-black text-2xl shadow-2xl border-4 border-background z-10">
                                1
                            </div>
                            <div className="glass-card rounded-2xl sm:rounded-3xl p-8 sm:p-10 pt-12 text-center border-2 border-border hover:border-primary/50 hover:-translate-y-2 transition-all duration-300 bg-gradient-to-br from-background to-secondary/20 h-full">
                                <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center group-hover:scale-110 transition-transform border-2 border-primary/30">
                                    <Upload className="w-10 h-10 text-primary" />
                                </div>
                                <h3 className="font-heading font-bold text-xl sm:text-2xl mb-3">Upload Image</h3>
                                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                                    Drag and drop or click to upload your image. Supports JPG, PNG, and WebP formats up to 10MB.
                                </p>
                            </div>
                        </div>

                        {/* Step 2 */}
                        <div className="relative group">
                            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white font-black text-2xl shadow-2xl border-4 border-background z-10">
                                2
                            </div>
                            <div className="glass-card rounded-2xl sm:rounded-3xl p-8 sm:p-10 pt-12 text-center border-2 border-border hover:border-primary/50 hover:-translate-y-2 transition-all duration-300 bg-gradient-to-br from-background to-secondary/20 h-full">
                                <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center group-hover:scale-110 transition-transform border-2 border-primary/30">
                                    <Sparkles className="w-10 h-10 text-primary" />
                                </div>
                                <h3 className="font-heading font-bold text-xl sm:text-2xl mb-3">AI Processes</h3>
                                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                                    Our advanced AI analyzes your image and removes the background in under 3 seconds with precision.
                                </p>
                            </div>
                        </div>

                        {/* Step 3 */}
                        <div className="relative group">
                            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white font-black text-2xl shadow-2xl border-4 border-background z-10">
                                3
                            </div>
                            <div className="glass-card rounded-2xl sm:rounded-3xl p-8 sm:p-10 pt-12 text-center border-2 border-border hover:border-primary/50 hover:-translate-y-2 transition-all duration-300 bg-gradient-to-br from-background to-secondary/20 h-full">
                                <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center group-hover:scale-110 transition-transform border-2 border-primary/30">
                                    <Download className="w-10 h-10 text-primary" />
                                </div>
                                <h3 className="font-heading font-bold text-xl sm:text-2xl mb-3">Download & Use</h3>
                                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                                    Download your high-quality transparent PNG and use it anywhere. No watermarks for registered users!
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* CTA after How It Works */}
                    <div className="mt-12 sm:mt-16 text-center">
                        <Button
                            size="lg"
                            className="rounded-full px-10 py-6 text-lg shadow-xl hover:shadow-2xl transition-all hover:scale-105"
                            onClick={() => navigate(user ? '/dashboard' : '/register')}
                        >
                            <Sparkles className="mr-2 w-5 h-5" />
                            Try It Now - It's Free!
                            <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                        <p className="text-sm text-muted-foreground mt-4">
                            No credit card required • {freeLimit} free credits • Takes less than 30 seconds
                        </p>
                    </div>
                </div>
            </section>

            {/* Social Proof Stats */}
            <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary/5 via-secondary/30 to-purple-500/5 border-y border-border relative overflow-hidden">
                {/* Background decoration */}
                <div className="absolute inset-0 opacity-20">
                    <div className="absolute top-0 left-1/4 w-64 h-64 bg-primary/20 rounded-full blur-3xl" />
                    <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl" />
                </div>

                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="text-center mb-8 sm:mb-10">
                        <h3 className="font-heading font-bold text-xl sm:text-2xl lg:text-3xl mb-2">
                            Trusted by Thousands Worldwide
                        </h3>
                        <p className="text-sm text-muted-foreground">Real results from real users</p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
                        <div className="group text-center p-6 sm:p-8 rounded-2xl bg-background/50 backdrop-blur-sm border-2 border-border hover:border-primary/50 hover:-translate-y-1 hover:shadow-2xl transition-all duration-300">
                            <div className="relative inline-block mb-3">
                                <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl group-hover:blur-2xl transition-all" />
                                <div className="relative font-heading font-black text-3xl sm:text-4xl lg:text-5xl bg-gradient-to-br from-primary via-primary to-purple-600 bg-clip-text text-transparent">
                                    10K+
                                </div>
                            </div>
                            <div className="text-sm sm:text-base font-semibold text-foreground mb-1">Happy Users</div>
                            <div className="text-xs text-muted-foreground">And growing daily</div>
                        </div>

                        <div className="group text-center p-6 sm:p-8 rounded-2xl bg-background/50 backdrop-blur-sm border-2 border-border hover:border-primary/50 hover:-translate-y-1 hover:shadow-2xl transition-all duration-300">
                            <div className="relative inline-block mb-3">
                                <div className="absolute inset-0 bg-green-500/20 rounded-full blur-xl group-hover:blur-2xl transition-all" />
                                <div className="relative font-heading font-black text-3xl sm:text-4xl lg:text-5xl bg-gradient-to-br from-green-600 via-green-500 to-emerald-600 bg-clip-text text-transparent">
                                    500K+
                                </div>
                            </div>
                            <div className="text-sm sm:text-base font-semibold text-foreground mb-1">Images Processed</div>
                            <div className="text-xs text-muted-foreground">Backgrounds removed</div>
                        </div>

                        <div className="group text-center p-6 sm:p-8 rounded-2xl bg-background/50 backdrop-blur-sm border-2 border-border hover:border-primary/50 hover:-translate-y-1 hover:shadow-2xl transition-all duration-300">
                            <div className="relative inline-block mb-3">
                                <div className="absolute inset-0 bg-amber-500/20 rounded-full blur-xl group-hover:blur-2xl transition-all" />
                                <div className="relative font-heading font-black text-3xl sm:text-4xl lg:text-5xl bg-gradient-to-br from-amber-500 via-amber-400 to-orange-500 bg-clip-text text-transparent flex items-center justify-center gap-1">
                                    4.9
                                    <Star className="w-6 h-6 sm:w-7 sm:h-7 fill-amber-500 text-amber-500 mb-1" />
                                </div>
                            </div>
                            <div className="text-sm sm:text-base font-semibold text-foreground mb-1">Rating</div>
                            <div className="text-xs text-muted-foreground">From 1,200+ reviews</div>
                        </div>

                        <div className="group text-center p-6 sm:p-8 rounded-2xl bg-background/50 backdrop-blur-sm border-2 border-border hover:border-primary/50 hover:-translate-y-1 hover:shadow-2xl transition-all duration-300 col-span-2 md:col-span-1">
                            <div className="relative inline-block mb-3">
                                <div className="absolute inset-0 bg-blue-500/20 rounded-full blur-xl group-hover:blur-2xl transition-all" />
                                <div className="relative font-heading font-black text-3xl sm:text-4xl lg:text-5xl bg-gradient-to-br from-blue-600 via-blue-500 to-cyan-600 bg-clip-text text-transparent">
                                    2.8s
                                </div>
                            </div>
                            <div className="text-sm sm:text-base font-semibold text-foreground mb-1">Avg. Processing</div>
                            <div className="text-xs text-muted-foreground">Lightning fast AI</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-background via-secondary/20 to-background">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12 sm:mb-16">
                        <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-primary/10 to-purple-500/10 text-sm font-bold uppercase tracking-widest text-primary mb-6 border-2 border-primary/20">
                            <Zap className="w-4 h-4" />
                            Why Choose Us
                        </span>
                        <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-4 sm:mb-6">
                            Professional Results.<br className="sm:hidden" /> Lightning Speed.
                        </h2>
                        <p className="text-base sm:text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                            Powered by cutting-edge AI technology trusted by <span className="font-bold text-primary">10,000+ professionals</span> worldwide
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10">
                        {features.map((feature, index) => (
                            <div
                                key={index}
                                className="group relative glass-card rounded-2xl sm:rounded-3xl p-8 sm:p-10 hover:-translate-y-2 hover:shadow-2xl transition-all duration-300 border-2 border-border hover:border-primary/50 bg-gradient-to-br from-background to-secondary/30"
                                data-testid={`feature-card-${index}`}
                            >
                                {/* Gradient glow on hover */}
                                <div className="absolute inset-0 rounded-2xl sm:rounded-3xl bg-gradient-to-br from-primary/0 via-purple-500/0 to-primary/0 group-hover:from-primary/10 group-hover:via-purple-500/10 group-hover:to-primary/10 transition-all duration-300 -z-10" />

                                <div className="relative">
                                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center text-primary mb-6 group-hover:scale-110 transition-transform duration-300 border-2 border-primary/30">
                                        {getIcon(feature.icon)}
                                    </div>
                                    <h3 className="font-heading font-bold text-xl sm:text-2xl mb-3 group-hover:text-primary transition-colors">
                                        {feature.title}
                                    </h3>
                                    <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                                        {feature.description}
                                    </p>
                                </div>

                                {/* Feature number badge */}
                                <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary border border-primary/20">
                                    {index + 1}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Additional mini features */}
                    <div className="mt-16 sm:mt-20 grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
                        <div className="text-center p-6 rounded-xl bg-secondary/30 border border-border hover:border-primary/30 transition-colors">
                            <ImageIcon className="w-8 h-8 mx-auto mb-3 text-primary" />
                            <h4 className="font-semibold text-sm mb-1">All Formats</h4>
                            <p className="text-xs text-muted-foreground">JPG, PNG, WebP</p>
                        </div>
                        <div className="text-center p-6 rounded-xl bg-secondary/30 border border-border hover:border-primary/30 transition-colors">
                            <Users className="w-8 h-8 mx-auto mb-3 text-primary" />
                            <h4 className="font-semibold text-sm mb-1">Batch Process</h4>
                            <p className="text-xs text-muted-foreground">Bulk uploads</p>
                        </div>
                        <div className="text-center p-6 rounded-xl bg-secondary/30 border border-border hover:border-primary/30 transition-colors">
                            <Sparkles className="w-8 h-8 mx-auto mb-3 text-primary" />
                            <h4 className="font-semibold text-sm mb-1">AI Precision</h4>
                            <p className="text-xs text-muted-foreground">99.9% accuracy</p>
                        </div>
                        <div className="text-center p-6 rounded-xl bg-secondary/30 border border-border hover:border-primary/30 transition-colors">
                            <TrendingUp className="w-8 h-8 mx-auto mb-3 text-primary" />
                            <h4 className="font-semibold text-sm mb-1">API Access</h4>
                            <p className="text-xs text-muted-foreground">For developers</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
                {/* Background decoration */}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary/50 to-transparent" />

                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12 sm:mb-16">
                        <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-amber-500/10 to-orange-500/10 text-sm font-bold uppercase tracking-widest text-amber-600 mb-6 border-2 border-amber-500/20">
                            <Star className="w-4 h-4 fill-amber-600" />
                            5-Star Reviews
                        </span>
                        <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-4 sm:mb-6">
                            Loved by Creators.<br className="sm:hidden" /> Trusted Worldwide.
                        </h2>
                        <p className="text-base sm:text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                            Join <span className="font-bold text-foreground">10,000+ satisfied users</span> who transformed their workflow
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                        {testimonials.map((testimonial, index) => (
                            <div
                                key={index}
                                className="group relative glass-card rounded-2xl sm:rounded-3xl p-6 sm:p-8 border-2 border-border hover:border-primary/50 hover:-translate-y-1 hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-background to-secondary/20"
                            >
                                {/* Quote decoration */}
                                <div className="absolute top-4 right-4 text-6xl text-primary/5 font-serif leading-none">"</div>

                                <div className="relative">
                                    <div className="flex items-center gap-1 mb-4">
                                        {[...Array(testimonial.rating)].map((_, i) => (
                                            <Star key={i} className="w-5 h-5 fill-amber-500 text-amber-500" />
                                        ))}
                                    </div>
                                    <p className="text-base sm:text-lg text-foreground mb-6 leading-relaxed font-medium">
                                        "{testimonial.content}"
                                    </p>
                                    <div className="flex items-center gap-4 pt-4 border-t border-border">
                                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center font-bold text-primary text-base border-2 border-primary/30">
                                            {testimonial.avatar}
                                        </div>
                                        <div>
                                            <div className="font-bold text-base">{testimonial.name}</div>
                                            <div className="text-sm text-muted-foreground">{testimonial.role}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Trust badges - Enhanced */}
                    <div className="mt-16 sm:mt-20 p-8 sm:p-10 rounded-2xl bg-gradient-to-br from-secondary/30 to-secondary/10 border-2 border-border">
                        <div className="flex flex-wrap items-center justify-center gap-8 sm:gap-16">
                            <div className="text-center group cursor-pointer">
                                <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-green-500/10 flex items-center justify-center group-hover:scale-110 transition-transform border-2 border-green-500/20">
                                    <Shield className="w-8 h-8 text-green-600" />
                                </div>
                                <h4 className="font-bold text-sm mb-1">SSL Encrypted</h4>
                                <p className="text-xs text-muted-foreground">Bank-level security</p>
                            </div>
                            <div className="text-center group cursor-pointer">
                                <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-blue-500/10 flex items-center justify-center group-hover:scale-110 transition-transform border-2 border-blue-500/20">
                                    <Lock className="w-8 h-8 text-blue-600" />
                                </div>
                                <h4 className="font-bold text-sm mb-1">GDPR Compliant</h4>
                                <p className="text-xs text-muted-foreground">Privacy guaranteed</p>
                            </div>
                            <div className="text-center group cursor-pointer">
                                <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-amber-500/10 flex items-center justify-center group-hover:scale-110 transition-transform border-2 border-amber-500/20">
                                    <Award className="w-8 h-8 text-amber-600" />
                                </div>
                                <h4 className="font-bold text-sm mb-1">SOC 2 Certified</h4>
                                <p className="text-xs text-muted-foreground">Enterprise ready</p>
                            </div>
                            <div className="text-center group cursor-pointer">
                                <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform border-2 border-primary/20">
                                    <Users className="w-8 h-8 text-primary" />
                                </div>
                                <h4 className="font-bold text-sm mb-1">10K+ Users</h4>
                                <p className="text-xs text-muted-foreground">Worldwide trust</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Use Cases */}
            <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8 bg-secondary/30">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
                        <div className="order-2 lg:order-1">
                            <span className="inline-block px-4 py-1.5 rounded-full bg-secondary text-xs font-medium uppercase tracking-widest text-muted-foreground mb-4">
                                Use Cases
                            </span>
                            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight mb-4 sm:mb-6">
                                Perfect for Every Project
                            </h2>
                            <p className="text-sm sm:text-base text-muted-foreground mb-6 sm:mb-8">
                                Whether you're running an online store, managing social media, or creating marketing materials, we've got you covered.
                            </p>
                            <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                                {useCases.map((item, index) => (
                                    <li key={index} className="flex items-center gap-3" data-testid={`use-case-${index}`}>
                                        <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                            <Check className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
                                        </div>
                                        <span className="text-sm sm:text-base text-foreground">{item}</span>
                                    </li>
                                ))}
                            </ul>
                            <Button
                                size="lg"
                                className="rounded-full w-full sm:w-auto"
                                onClick={() => navigate(user ? '/dashboard' : '/register')}
                            >
                                Get Started Now
                                <ArrowRight className="ml-2 w-4 h-4" />
                            </Button>
                        </div>
                        <div className="order-1 lg:order-2 grid grid-cols-2 gap-3 sm:gap-4">
                            <img
                                src={featureImage1}
                                alt="Product photography"
                                className="rounded-xl sm:rounded-2xl shadow-lg w-full h-40 sm:h-48 object-cover hover:scale-105 transition-transform duration-300"
                                data-testid="usecase-image-1"
                            />
                            <img
                                src={featureImage2}
                                alt="Social content"
                                className="rounded-xl sm:rounded-2xl shadow-lg w-full h-40 sm:h-48 object-cover mt-4 sm:mt-8 hover:scale-105 transition-transform duration-300"
                                data-testid="usecase-image-2"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl mx-auto">
                    <div className="text-center mb-8 sm:mb-12">
                        <span className="inline-block px-4 py-1.5 rounded-full bg-secondary text-xs font-medium uppercase tracking-widest text-muted-foreground mb-4">
                            FAQ
                        </span>
                        <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight mb-3">
                            Frequently Asked Questions
                        </h2>
                        <p className="text-sm sm:text-base text-muted-foreground">
                            Got questions? We've got answers
                        </p>
                    </div>

                    <div className="space-y-3 sm:space-y-4">
                        {faqs.map((faq, index) => (
                            <div
                                key={index}
                                className="glass-card rounded-xl border border-border overflow-hidden"
                            >
                                <button
                                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                                    className="w-full px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between text-left hover:bg-secondary/30 transition-colors"
                                >
                                    <span className="font-semibold text-sm sm:text-base pr-4">{faq.question}</span>
                                    <ChevronDown
                                        className={`w-5 h-5 text-muted-foreground transition-transform flex-shrink-0 ${
                                            openFaq === index ? 'rotate-180' : ''
                                        }`}
                                    />
                                </button>
                                {openFaq === index && (
                                    <div className="px-4 sm:px-6 pb-4 sm:pb-5 text-sm sm:text-base text-muted-foreground">
                                        {faq.answer}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing Preview Section */}
            <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-background via-secondary/30 to-background relative overflow-hidden">
                {/* Decorative elements */}
                <div className="absolute top-20 left-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />

                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="text-center mb-12 sm:mb-16">
                        <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-primary/10 to-purple-500/10 text-sm font-bold uppercase tracking-widest text-primary mb-6 border-2 border-primary/20">
                            <Crown className="w-4 h-4" />
                            Simple Pricing
                        </span>
                        <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-4 sm:mb-6">
                            Choose Your Perfect Plan
                        </h2>
                        <p className="text-base sm:text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                            Start free, upgrade anytime. <span className="font-bold text-foreground">No hidden fees.</span> Cancel whenever you want.
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto mb-8">
                        {/* Starter Plan - 50 credits */}
                        <div className="rounded-2xl p-6 border-2 border-border bg-card hover:border-primary/50 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                            <div className="text-center mb-4">
                                <div className="mb-2">
                                    <span className="text-xs font-semibold text-primary uppercase tracking-wide">Starter</span>
                                </div>
                                <div className="text-4xl font-bold mb-1">50</div>
                                <div className="text-sm text-muted-foreground">credits/month</div>
                                <div className="text-xs mt-1 text-muted-foreground">Perfect for individuals</div>
                            </div>
                            <div className="text-center mb-6">
                                <div className="text-3xl font-bold">${4.99}</div>
                                <div className="text-xs text-muted-foreground">USD per month</div>
                                <div className="text-xs mt-2 px-3 py-1 rounded-full inline-block bg-primary/10 text-primary font-semibold">
                                    $0.10/credit
                                </div>
                            </div>
                            <div className="space-y-2 mb-6">
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <Check className="w-4 h-4 text-primary" />
                                    <span>Credits rollover monthly</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <Check className="w-4 h-4 text-primary" />
                                    <span>HD quality exports</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <Check className="w-4 h-4 text-primary" />
                                    <span>Cancel anytime</span>
                                </div>
                            </div>
                            <Button
                                className="w-full rounded-full"
                                onClick={() => navigate('/pricing')}
                            >
                                Choose Starter
                            </Button>
                        </div>

                        {/* Most Popular Plan - 100 credits */}
                        <div className="relative rounded-2xl p-6 border-2 border-primary bg-gradient-to-br from-primary via-primary/95 to-primary/90 text-primary-foreground shadow-2xl scale-105 hover:scale-110 transition-all duration-300">
                            <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20">
                                <span className="px-4 py-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold uppercase shadow-lg whitespace-nowrap">
                                    ⭐ Most Popular
                                </span>
                            </div>
                            <div className="text-center mb-4">
                                <div className="text-4xl font-bold mb-1 text-white">100</div>
                                <div className="text-sm text-primary-foreground/80">credits/month</div>
                                <div className="text-xs mt-1 text-primary-foreground/70">Best for freelancers</div>
                            </div>
                            <div className="text-center mb-6">
                                <div className="text-3xl font-bold text-white">${8.99}</div>
                                <div className="text-xs text-primary-foreground/70">USD per month</div>
                                <div className="text-xs mt-2 px-3 py-1 rounded-full inline-block bg-white/20 text-white font-semibold">
                                    $0.09/credit
                                </div>
                            </div>
                            <div className="space-y-2 mb-6">
                                <div className="flex items-center gap-2 text-xs text-primary-foreground/90">
                                    <Check className="w-4 h-4 text-white" />
                                    <span>Credits rollover monthly</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-primary-foreground/90">
                                    <Check className="w-4 h-4 text-white" />
                                    <span>HD quality exports</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-primary-foreground/90">
                                    <Check className="w-4 h-4 text-white" />
                                    <span>Cancel anytime</span>
                                </div>
                            </div>
                            <Button
                                variant="secondary"
                                className="w-full rounded-full shadow-xl hover:shadow-2xl"
                                onClick={() => navigate('/pricing')}
                            >
                                Get Started
                            </Button>
                        </div>

                        {/* Professional Plan - 300 credits */}
                        <div className="rounded-2xl p-6 border-2 border-border bg-card hover:border-primary/50 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                            <div className="text-center mb-4">
                                <div className="mb-2">
                                    <span className="text-xs font-semibold text-primary uppercase tracking-wide">Professional</span>
                                </div>
                                <div className="text-4xl font-bold mb-1">300</div>
                                <div className="text-sm text-muted-foreground">credits/month</div>
                                <div className="text-xs mt-1 text-muted-foreground">For busy creatives</div>
                            </div>
                            <div className="text-center mb-6">
                                <div className="text-3xl font-bold">${19.99}</div>
                                <div className="text-xs text-muted-foreground">USD per month</div>
                                <div className="text-xs mt-2 px-3 py-1 rounded-full inline-block bg-primary/10 text-primary font-semibold">
                                    $0.07/credit
                                </div>
                            </div>
                            <div className="space-y-2 mb-6">
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <Check className="w-4 h-4 text-primary" />
                                    <span>Credits rollover monthly</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <Check className="w-4 h-4 text-primary" />
                                    <span>HD quality exports</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <Check className="w-4 h-4 text-primary" />
                                    <span>Cancel anytime</span>
                                </div>
                            </div>
                            <Button
                                variant="outline"
                                className="w-full rounded-full"
                                onClick={() => navigate('/pricing')}
                            >
                                Choose Professional
                            </Button>
                        </div>
                    </div>

                    {/* Free tier callout */}
                    <div className="max-w-3xl mx-auto mb-8">
                        <div className="glass-card rounded-2xl p-6 border-2 border-green-500/30 bg-gradient-to-br from-green-500/5 to-transparent text-center">
                            <div className="flex items-center justify-center gap-3 flex-wrap">
                                <Sparkles className="w-6 h-6 text-green-600" />
                                <p className="text-base sm:text-lg">
                                    <span className="font-bold text-foreground">Start Free:</span> Get <span className="font-bold text-green-600">{freeLimit} free credits</span> • No credit card • No sign-up required
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="mt-12 text-center">
                        <Button
                            variant="link"
                            className="text-primary hover:text-primary/80"
                            onClick={() => navigate('/pricing')}
                        >
                            View all plans and features
                            <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                    </div>
                </div>
            </section>

            {/* Final CTA Section */}
            <section className="relative py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary via-primary to-purple-600 text-primary-foreground overflow-hidden">
                {/* Animated background elements */}
                <div className="absolute inset-0">
                    <div className="absolute top-0 left-0 w-full h-full opacity-10">
                        <div className="absolute inset-0" style={{
                            backgroundImage: 'radial-gradient(circle at 2px 2px, white 2px, transparent 0)',
                            backgroundSize: '40px 40px'
                        }} />
                    </div>
                    <div className="absolute top-10 left-10 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
                    <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-300/10 rounded-full blur-3xl" />
                </div>

                <div className="max-w-5xl mx-auto text-center relative z-10">
                    {/* Trust badge */}
                    <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/20 backdrop-blur-sm text-sm font-bold mb-8 border-2 border-white/30 shadow-2xl">
                        <TrendingUp className="w-4 h-4" />
                        <span>🚀 Join 10,000+ satisfied creators</span>
                    </div>

                    {/* Headline */}
                    <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl xl:text-6xl tracking-tight leading-[1.1] mb-6 sm:mb-8">
                        <span className="block mb-2">Ready to Transform</span>
                        <span className="block bg-gradient-to-r from-white via-white to-purple-100 bg-clip-text text-transparent">
                            Your Images Forever?
                        </span>
                    </h2>

                    {/* Subheadline */}
                    <p className="text-lg sm:text-xl lg:text-2xl text-primary-foreground/90 mb-10 sm:mb-12 max-w-3xl mx-auto px-4 leading-relaxed">
                        Start with <span className="font-black text-white">{freeLimit} free credits</span> — no sign-up needed.
                        Upgrade anytime to unlock unlimited power starting at <span className="font-black text-white">$4.99/month</span>.
                    </p>

                    {/* CTA Buttons */}
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5 mb-10">
                        <Button
                            size="lg"
                            variant="secondary"
                            className="group rounded-full px-10 sm:px-14 py-7 sm:py-8 text-lg sm:text-xl shadow-2xl hover:shadow-[0_20px_60px_rgba(0,0,0,0.3)] transition-all w-full sm:w-auto transform hover:scale-105 font-bold"
                            onClick={() => navigate(user ? '/dashboard' : '/register')}
                            data-testid="cta-get-started-btn"
                        >
                            <Sparkles className="mr-2 w-6 h-6 group-hover:rotate-12 transition-transform" />
                            Try Now - 100% Free
                            <ArrowRight className="ml-2 w-6 h-6 group-hover:translate-x-1 transition-transform" />
                        </Button>
                        <Button
                            size="lg"
                            variant="outline"
                            className="rounded-full px-10 sm:px-12 py-7 sm:py-8 text-lg sm:text-xl border-2 border-white/40 hover:bg-white/20 text-white w-full sm:w-auto backdrop-blur-sm font-bold"
                            onClick={() => navigate('/pricing')}
                        >
                            <Crown className="mr-2 w-6 h-6" />
                            View All Plans
                        </Button>
                    </div>

                    {/* Trust indicators */}
                    <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 text-sm sm:text-base text-white/90 mb-10">
                        <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
                            <Check className="w-5 h-5 text-green-300" />
                            <span className="font-semibold">No credit card</span>
                        </div>
                        <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
                            <Check className="w-5 h-5 text-green-300" />
                            <span className="font-semibold">Cancel anytime</span>
                        </div>
                        <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
                            <Check className="w-5 h-5 text-green-300" />
                            <span className="font-semibold">24/7 support</span>
                        </div>
                    </div>

                    {/* Stats showcase */}
                    <div className="grid grid-cols-3 gap-6 sm:gap-10 max-w-3xl mx-auto pt-8 border-t border-white/20">
                        <div className="text-center">
                            <div className="text-3xl sm:text-4xl lg:text-5xl font-black mb-2 text-white">2.8s</div>
                            <div className="text-xs sm:text-sm text-primary-foreground/80 font-medium">Avg. Processing</div>
                        </div>
                        <div className="text-center">
                            <div className="text-3xl sm:text-4xl lg:text-5xl font-black mb-2 text-white">500K+</div>
                            <div className="text-xs sm:text-sm text-primary-foreground/80 font-medium">Images Removed</div>
                        </div>
                        <div className="text-center">
                            <div className="text-3xl sm:text-4xl lg:text-5xl font-black mb-2 text-white flex items-center justify-center gap-1">
                                4.9
                                <Star className="w-6 h-6 sm:w-7 sm:h-7 fill-amber-300 text-amber-300" />
                            </div>
                            <div className="text-xs sm:text-sm text-primary-foreground/80 font-medium">User Rating</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-6 sm:py-8 px-4 sm:px-6 lg:px-8 border-t border-border bg-background">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                            {logoUrl ? (
                                <img
                                    src={logoUrl.startsWith('http') ? logoUrl : `${process.env.REACT_APP_BACKEND_URL}${logoUrl}`}
                                    alt={businessName}
                                    className="w-6 h-6 rounded object-contain"
                                />
                            ) : (
                                <div className="w-6 h-6 rounded bg-primary flex items-center justify-center">
                                    <Upload className="w-3 h-3 text-primary-foreground" />
                                </div>
                            )}
                            <span className="font-heading font-semibold text-sm sm:text-base">{businessName}</span>
                        </div>

                        <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-xs sm:text-sm text-muted-foreground">
                            <Link to="/pricing" className="hover:text-foreground transition-colors">
                                Pricing
                            </Link>
                            <Link to="/privacy" className="hover:text-foreground transition-colors">
                                Privacy
                            </Link>
                            <Link to="/terms" className="hover:text-foreground transition-colors">
                                Terms
                            </Link>
                            <Link to="/disclaimer" className="hover:text-foreground transition-colors">
                                Disclaimer
                            </Link>
                            <Link to="/contact" className="hover:text-foreground transition-colors">
                                Contact
                            </Link>
                        </div>
                    </div>

                    <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-border text-center space-y-3">
                        <p className="text-xs sm:text-sm text-muted-foreground">
                            {footerText}
                        </p>
                        <p className="text-xs text-muted-foreground/80 max-w-3xl mx-auto">
                            <strong className="text-foreground">Image Ownership:</strong> All images uploaded to {businessName} belong to their rightful owners and are not owned by us.
                            Unlike other services that claim ownership of your files, we respect your intellectual property rights.
                            Your images are processed solely for background removal and are automatically deleted after processing.
                            See our{' '}
                            <Link to="/disclaimer" className="text-primary hover:underline">
                                Disclaimer
                            </Link>
                            {' '}for more information.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Landing;
